import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LabelList,
} from 'recharts';
import { formatValue, resolveKey } from '../chartUtils';
import type { ChartConfig } from '../chartUtils';
import { TOOLTIP_STYLE, GRID_STROKE, AXIS_TICK } from './constants';

export default function LineChartView({ data, config, fullscreen = false }: { data: Record<string, unknown>[]; config: ChartConfig; fullscreen?: boolean }) {
  const xKey = resolveKey(config, 'x', data);
  const yKey = resolveKey(config, 'y', data);
  const color = config.color || '#ff6738';
  const needsRotation = data.length > 5;
  const chartHeight = fullscreen ? '100%' : (config.height || 300);

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <LineChart data={data} margin={{ top: 5, right: 20, left: 10, bottom: needsRotation ? 80 : 40 }}>
        {config.show_grid !== false && <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />}
        <XAxis
          dataKey={xKey}
          tick={AXIS_TICK}
          angle={needsRotation ? -35 : 0}
          textAnchor={needsRotation ? 'end' : 'middle'}
          height={needsRotation ? 80 : 40}
          interval={0}
          label={
            config.x_label
              ? { value: config.x_label, position: 'insideBottom', offset: -5, fill: '#888', fontSize: 12 }
              : undefined
          }
        />
        <YAxis
          tick={AXIS_TICK}
          tickFormatter={(v) => formatValue(v, config)}
          domain={[config.min_value ?? 'auto', config.max_value ?? 'auto']}
          label={
            config.y_label
              ? { value: config.y_label, angle: -90, position: 'insideLeft', fill: '#888', fontSize: 12 }
              : undefined
          }
        />
        <Tooltip {...TOOLTIP_STYLE} formatter={(value: unknown) => formatValue(value, config)} />
        {config.show_legend && <Legend wrapperStyle={{ fontSize: '12px', color: '#999' }} />}
        <Line
          type="monotone"
          dataKey={yKey}
          stroke={color}
          strokeWidth={2}
          dot={{ fill: color, r: 3 }}
          activeDot={{ r: 5 }}
          name={config.y_label || yKey}
        >
          {config.show_values && (
            <LabelList
              dataKey={yKey}
              position="top"
              formatter={(v: unknown) => formatValue(v, config)}
              style={{ fill: '#ccc', fontSize: 10 }}
            />
          )}
        </Line>
      </LineChart>
    </ResponsiveContainer>
  );
}
